import type { NormalizedConversation, NormalizedItem, NormalizedUser, RoundEnrichment } from "./types";

/**
 * The post-fetch, pre-normalize view of a group: attribution facts resolved
 * (worker canonical, user via resolveIdentity, a2a caller/target/delegationId)
 * plus the conversation-level rollup from groupIntoConversations. `recoveredVia`
 * is set by the pipeline ONLY when the conversationId came from trace recovery.
 */
export interface NormalizeGroup {
  conversationId: string;
  source: string;
  worker: string;
  user?: NormalizedUser;
  caller?: string;
  target?: string;
  delegationId?: string;
  startedAt: string;
  lastAt: string;
  rounds: number;
  outcome: string;
  recoveredVia?: "trace";
}

/**
 * §5/§7 — assemble a NormalizedConversation. STRUCTURED user (not flattened:
 * PII + misrouting need source/ref/display). `items` are oldest-first (the
 * caller has already reversed the newest-first SDK page). `enrichment` is the
 * merged trace round, omitted when the trace degraded to ledger-only.
 *
 * A `recovered` outcome is content-proven (items.length >= 1) by the CALLER
 * (classifyGroup, T18); normalize carries the items it is given verbatim.
 * Pure — no clock, no I/O.
 */
export function normalize(
  group: NormalizeGroup,
  items: NormalizedItem[],
  enrichment?: RoundEnrichment,
): NormalizedConversation {
  const conv: NormalizedConversation = {
    conversationId: group.conversationId,
    source: group.source,
    attribution: {
      worker: group.worker,
      ...(group.user ? { user: group.user } : {}),
      ...(group.caller ? { caller: group.caller } : {}),
      ...(group.target ? { target: group.target } : {}),
      ...(group.delegationId ? { delegationId: group.delegationId } : {}),
    },
    startedAt: group.startedAt,
    lastAt: group.lastAt,
    rounds: group.rounds,
    outcome: group.outcome,
    items,
  };
  if (enrichment) {
    conv.enrichment = {
      inputTokens: enrichment.inputTokens,
      outputTokens: enrichment.outputTokens,
      tools: enrichment.tools,
    };
  }
  if (group.recoveredVia) conv.recoveredVia = group.recoveredVia;
  return conv;
}
